import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reviewSmsApi } from '../api/reviewSmsApi';
import { reviewKeys } from '../queryKeys';
import { useLocationId } from '../sms/hooks';
import { Delta, Kpi, RangeSwitch } from './AnalyticsCards';
import type { AnalyticsRange } from './types';
import styles from './analytics.module.css';

/**
 * The top of Get reviews: reviews from both methods together. A practice asks one way at a time,
 * but printed QR codes keep working while it texts, so both can bring in reviews in one period.
 */
export function ReviewsSummaryStrip({ onFollowUp }: { onFollowUp: () => void }) {
  const locationId = useLocationId();
  const [range, setRange] = useState<AnalyticsRange>('30d');
  const summary = useQuery({
    queryKey: reviewKeys.smsSummary(locationId, range),
    queryFn: () => reviewSmsApi.getSummary(locationId, range),
    enabled: !!locationId,
    staleTime: 60_000,
  });
  const s = summary.data;
  if (!s) return null;
  return (
    <div className={styles.stack}>
      <div className={styles.toolbar}>
        <span className={styles.eyebrow}>All review requests · last {range}</span>
        <RangeSwitch value={range} onChange={setRange} />
      </div>
      <div className={styles.kpis}>
        <Kpi
          label="New Google reviews"
          value={s.newReviews.total}
          sub={
            <>
              <Delta
                current={s.newReviews.total}
                previous={s.newReviews.previousTotal}
                unit="count"
              />{' '}
              vs the {range} before
            </>
          }
        />
        <Kpi
          label="From texts"
          value={s.newReviews.text}
          accent
          sub={`${s.texted} patients texted`}
        />
        <Kpi
          label="From link & QR"
          value={s.newReviews.qr}
          sub={`${s.qrScans} scans · printed codes keep working`}
        />
        <Kpi
          label="Private feedback"
          value={s.privateFeedback.total}
          sub={
            s.privateFeedback.needFollowUp > 0 ? (
              <button type="button" className={styles.linkButton} onClick={onFollowUp}>
                {s.privateFeedback.needFollowUp} conversation
                {s.privateFeedback.needFollowUp === 1 ? '' : 's'} need you →
              </button>
            ) : (
              'nothing waiting on you'
            )
          }
        />
      </div>
    </div>
  );
}
